import { ChangeEvent, useState } from 'react';
import {
    Checkbox,
    FormControlLabel,
    styled,
    TextField,
    Typography,
    Box,
    Select,
    MenuItem,
    FormLabel,
    Radio,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import {
    CURRENT_INSCRIPTION_YEAR,
    firstInscriptionYearsMapping,
    INSCRIPTION_FEE,
    SELECTABLE_FIRST_INSCRIPTION_YEARS,
    selectableFirstInscriptionYearType,
    yearMapping,
    YEARS,
    yearType,
} from '../constants';
import { paymentModeType } from '../types';
import { TransferDetails } from '../components/TransferDetails';
import { computePrices } from '../lib/computePrices';
import { PaymentModeLabel } from '../components/PaymentModeLabel';
import { computeReadableDate } from '../../../lib/date';
import { EsajSignature } from '../components/EsajSignature';
import { FormLayout } from '../../../components/FormLayout';
import { Section } from '../../../components/Section';
import { Button } from '../../../components/Button';
import { useApiCall } from '../../../lib/useApiCall';
import { api } from '../../../lib/api';
import { pathHandler } from '../../../lib/pathHandler';
import { AmountDisclaimer } from '../components/AmountDisclaimer';

function FicheInscription() {
    const [signatureFile, setSignatureFile] = useState<File | null>(null);
    const [formTextData, setTextFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        ineNumber: '',
        rfFirstName: '',
        rfLastName: '',
        rfAddress: '',
        rfZipCode: '',
        rfCity: '',
        rfHomePhoneNumber: '',
        rfMobilePhoneNumber: '',
        rfEmail: '',
        rfJob: '',
        rfJobPhoneNumber: '',
        rf2FirstName: '',
        rf2LastName: '',
        rf2Address: '',
        rf2ZipCode: '',
        rf2City: '',
        rf2HomePhoneNumber: '',
        rf2MobilePhoneNumber: '',
        rf2Email: '',
        rf2Job: '',
        rf2JobPhoneNumber: '',
        place: '',
    });
    const [wasRegisteredInEsajBefore, setWasRegisteredInEsajBefore] = useState<
        boolean | undefined
    >();
    const [hasRf2, setHasRf2] = useState(false);
    const [selectedPromo, setSelectedPromo] = useState<yearType | undefined>();

    const [selectedPaymentMode, setSelectedPaymentMode] = useState<paymentModeType | undefined>(
        undefined,
    );
    const [firstInscriptionYear, setFirstInscriptionYear] = useState<
        selectableFirstInscriptionYearType | undefined
    >(undefined);
    const [hasAlreadyAnEsajMaster, setHasAlreadyAnEsajMaster] = useState(false);
    const [hasAcknowledgedWholeYearIsDue, setHasAcknowledgedWholeYearIsDue] = useState(false);
    const [hasAcknowledgedPricing, setHasAcknowledgedPricing] = useState(false);
    const [hasAcknowledgedCanBeTemporarilyExpelled, setHasAcknowledgedCanBeTemporarilyExpelled] =
        useState(false);
    const navigate = useNavigate();

    const createFicheInscriptionEntryApiCall = useApiCall({
        apiCall: api.createFicheInscriptionEntry,
        onSuccess: (data) => {
            navigate(
                pathHandler.getRoutePath('FICHE_INSCRIPTION_PREVIEW', {
                    ficheInscriptionId: data.id,
                }),
            );
        },
    });
    const prices =
        selectedPromo &&
        ((wasRegisteredInEsajBefore && firstInscriptionYear !== undefined) ||
            wasRegisteredInEsajBefore === false)
            ? computePrices({
                  selectedPromo,
                  hasAlreadyAnEsajMaster,
                  firstInscriptionYear: firstInscriptionYear || CURRENT_INSCRIPTION_YEAR,
              })
            : undefined;

    const readableDate = computeReadableDate(new Date());

    return (
        <FormLayout handleSubmit={handleSubmit} title="BACHELOR / MASTER" subtitle={`Inscription`}>
            <CenteredRow>
                <StyledFormControlLabel
                    control={<Radio checked={wasRegisteredInEsajBefore === false} />}
                    onChange={(_, checked) => {
                        setWasRegisteredInEsajBefore(!checked);
                        setFirstInscriptionYear(undefined);
                        setHasAlreadyAnEsajMaster(false);
                    }}
                    label="Il s'agit de ma première inscription"
                    name={`no-previous-inscription-year-option`}
                />
            </CenteredRow>
            <CenteredRow>
                <StyledFormControlLabel
                    control={<Radio checked={wasRegisteredInEsajBefore === true} />}
                    onChange={(_, checked) => setWasRegisteredInEsajBefore(checked)}
                    label="Il s'agit d'une réinscription"
                    name={`previous-inscription-year-option`}
                />
            </CenteredRow>
            {wasRegisteredInEsajBefore && (
                <CenteredRow>
                    <Typography>Ma première inscription date * : </Typography>
                    <Box minWidth={'14vw'}>
                        <Select
                            fullWidth
                            disabled={!wasRegisteredInEsajBefore}
                            required={wasRegisteredInEsajBefore}
                            value={firstInscriptionYear}
                            onChange={(e) =>
                                setFirstInscriptionYear(
                                    e.target.value as selectableFirstInscriptionYearType,
                                )
                            }
                        >
                            {SELECTABLE_FIRST_INSCRIPTION_YEARS.map((PREVIOUS_INSCRIPTION_YEAR) => (
                                <MenuItem value={PREVIOUS_INSCRIPTION_YEAR}>
                                    {firstInscriptionYearsMapping[PREVIOUS_INSCRIPTION_YEAR].label}
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>
                </CenteredRow>
            )}

            <Section variant="h3" title="Année d'inscription">
                <CenteredRow>
                    <Typography>Je m'inscris en * : </Typography>
                    <Box minWidth={'14vw'}>
                        <Select
                            fullWidth
                            required
                            value={selectedPromo}
                            onChange={(e) => setSelectedPromo(e.target.value as yearType)}
                        >
                            {YEARS.map((YEAR) => (
                                <MenuItem value={YEAR}>{yearMapping[YEAR].label}</MenuItem>
                            ))}
                        </Select>
                    </Box>
                </CenteredRow>
                {selectedPromo === '5A-ecologue' && (
                    <CenteredRow>
                        <StyledFormControlLabel
                            control={<Checkbox checked={hasAlreadyAnEsajMaster} />}
                            onChange={(_, checked) => setHasAlreadyAnEsajMaster(checked)}
                            label="Je suis déjà titulaire d'un master Architecte du paysage"
                            name={`has-already-an-esaj-a-master`}
                        />
                    </CenteredRow>
                )}
            </Section>

            <Section variant="h2" title="Informations personnelles">
                <Row>
                    <TextField
                        fullWidth
                        onChange={handleFormChange}
                        required
                        label="Prénom"
                        placeholder="Jeanne"
                        name="firstName"
                        value={formTextData.firstName}
                    />
                    <TextField
                        fullWidth
                        onChange={handleFormChange}
                        required
                        label="Nom"
                        placeholder="Dupont"
                        name="lastName"
                        value={formTextData.lastName}
                    />
                </Row>
                <Row>
                    <TextField
                        fullWidth
                        onChange={handleFormChange}
                        required
                        label="Numéro INE"
                        placeholder="XXXXXXXXXXX"
                        name="ineNumber"
                        value={formTextData.ineNumber}
                    />
                    <TextField
                        fullWidth
                        onChange={handleFormChange}
                        required
                        label="Adresse e-mail"
                        placeholder="jeanne.dupont@email.com"
                        name="email"
                        value={formTextData.email}
                    />
                </Row>
            </Section>
            <Section
                variant="h2"
                title="Informations du/de la responsable financier(e)"
                description="Même si l’étudiant(e) assure le paiement des frais de scolarité, il/elle doit
                    avoir un(e) répondant(e) financier(e)."
            >
                <Row>
                    <TextFieldContainer width="25%">
                        <TextField
                            fullWidth
                            onChange={handleFormChange}
                            required
                            label="Prénom"
                            placeholder="Yvan"
                            name="rfFirstName"
                            value={formTextData.rfFirstName}
                        />
                    </TextFieldContainer>
                    <TextFieldContainer width="25%">
                        <TextField
                            fullWidth
                            onChange={handleFormChange}
                            required
                            label="Nom"
                            placeholder="Martin"
                            name="rfLastName"
                            value={formTextData.rfLastName}
                        />
                    </TextFieldContainer>
                    <TextFieldContainer width="50%">
                        <TextField
                            fullWidth
                            onChange={handleFormChange}
                            required
                            label="E-mail"
                            placeholder="yvan.martin@email.com"
                            name="rfEmail"
                            value={formTextData.rfEmail}
                        />
                    </TextFieldContainer>
                </Row>
                <Row>
                    <TextFieldContainer width="50%">
                        <TextField
                            fullWidth
                            onChange={handleFormChange}
                            required
                            label="Adresse postale"
                            placeholder="1 rue des acacias"
                            name="rfAddress"
                            value={formTextData.rfAddress}
                        />
                    </TextFieldContainer>

                    <TextFieldContainer width="15%">
                        <TextField
                            fullWidth
                            onChange={handleFormChange}
                            required
                            label="Code postal"
                            placeholder="01400"
                            name="rfZipCode"
                            value={formTextData.rfZipCode}
                        />
                    </TextFieldContainer>
                    <TextFieldContainer width="35%">
                        <TextField
                            fullWidth
                            onChange={handleFormChange}
                            required
                            label="Ville"
                            placeholder="Romans"
                            name="rfCity"
                            value={formTextData.rfCity}
                        />
                    </TextFieldContainer>
                </Row>
                <Row>
                    <TextFieldContainer width="25%">
                        <TextField
                            fullWidth
                            onChange={handleFormChange}
                            required
                            type="tel"
                            label="Téléphone portable"
                            placeholder="06 75 00 00 00"
                            name="rfMobilePhoneNumber"
                            value={formTextData.rfMobilePhoneNumber}
                        />
                    </TextFieldContainer>
                    <TextFieldContainer width="25%">
                        <TextField
                            fullWidth
                            type="tel"
                            onChange={handleFormChange}
                            required
                            label="Téléphone professionnel"
                            placeholder="01 75 00 00 00"
                            name="rfJobPhoneNumber"
                            value={formTextData.rfJobPhoneNumber}
                        />
                    </TextFieldContainer>
                    <TextFieldContainer width="50%">
                        <TextField
                            fullWidth
                            onChange={handleFormChange}
                            required
                            label="Profession"
                            placeholder="Infirmier"
                            name="rfJob"
                            value={formTextData.rfJob}
                        />
                    </TextFieldContainer>
                </Row>
                <Row>
                    {!hasRf2 && (
                        <Button
                            startIcon={<AddIcon />}
                            fullWidth
                            variant="outlined"
                            onClick={() => setHasRf2(true)}
                        >
                            Ajouter un(e) deuxième référent(e) financier(e)
                        </Button>
                    )}
                    {hasRf2 && (
                        <Button
                            startIcon={<ClearIcon />}
                            fullWidth
                            color="error"
                            variant="outlined"
                            onClick={() => setHasRf2(false)}
                        >
                            Supprimer le/la deuxième référent(e) financier(e)
                        </Button>
                    )}
                </Row>
            </Section>
            {hasRf2 && (
                <Section
                    variant="h2"
                    title="Informations du/de la deuxième responsable financier(e)"
                >
                    <Row>
                        <TextFieldContainer width="25%">
                            <TextField
                                fullWidth
                                onChange={handleFormChange}
                                required={hasRf2}
                                label="Prénom"
                                placeholder="Yvan"
                                name="rf2FirstName"
                                value={formTextData.rf2FirstName}
                            />
                        </TextFieldContainer>
                        <TextFieldContainer width="25%">
                            <TextField
                                fullWidth
                                onChange={handleFormChange}
                                required={hasRf2}
                                label="Nom"
                                placeholder="Martin"
                                name="rf2LastName"
                                value={formTextData.rf2LastName}
                            />
                        </TextFieldContainer>
                        <TextFieldContainer width="50%">
                            <TextField
                                fullWidth
                                onChange={handleFormChange}
                                required={hasRf2}
                                label="E-mail"
                                placeholder="yvan.martin@email.com"
                                name="rf2Email"
                                value={formTextData.rf2Email}
                            />
                        </TextFieldContainer>
                    </Row>
                    <Row>
                        <TextFieldContainer width="50%">
                            <TextField
                                fullWidth
                                onChange={handleFormChange}
                                required={hasRf2}
                                label="Adresse postale"
                                placeholder="1 rue des acacias"
                                name="rf2Address"
                                value={formTextData.rf2Address}
                            />
                        </TextFieldContainer>

                        <TextFieldContainer width="15%">
                            <TextField
                                fullWidth
                                onChange={handleFormChange}
                                required={hasRf2}
                                label="Code postal"
                                placeholder="01400"
                                name="rf2ZipCode"
                                value={formTextData.rf2ZipCode}
                            />
                        </TextFieldContainer>
                        <TextFieldContainer width="35%">
                            <TextField
                                fullWidth
                                onChange={handleFormChange}
                                required={hasRf2}
                                label="Ville"
                                placeholder="Romans"
                                name="rf2City"
                                value={formTextData.rf2City}
                            />
                        </TextFieldContainer>
                    </Row>
                    <Row>
                        <TextFieldContainer width="25%">
                            <TextField
                                fullWidth
                                onChange={handleFormChange}
                                required={hasRf2}
                                type="tel"
                                label="Téléphone portable"
                                placeholder="06 75 00 00 00"
                                name="rf2MobilePhoneNumber"
                                value={formTextData.rf2MobilePhoneNumber}
                            />
                        </TextFieldContainer>
                        <TextFieldContainer width="25%">
                            <TextField
                                fullWidth
                                type="tel"
                                onChange={handleFormChange}
                                required={hasRf2}
                                label="Téléphone professionnel"
                                placeholder="01 75 00 00 00"
                                name="rf2JobPhoneNumber"
                                value={formTextData.rf2JobPhoneNumber}
                            />
                        </TextFieldContainer>
                        <TextFieldContainer width="50%">
                            <TextField
                                fullWidth
                                onChange={handleFormChange}
                                required={hasRf2}
                                label="Profession"
                                placeholder="Infirmier"
                                name="rf2Job"
                                value={formTextData.rf2Job}
                            />
                        </TextFieldContainer>
                    </Row>
                </Section>
            )}
            {!!prices && (
                <Section
                    variant="h2"
                    title="Modalités de paiement"
                    description={
                        !wasRegisteredInEsajBefore
                            ? "Le montant inclut les frais de scolarité et les frais de dossier (150 € non remboursables en cas de désistement avant le début de l'année académique)."
                            : undefined
                    }
                    subDescription="Cochez la case pour confirmer la modalité de paiement souhaitée"
                >
                    <Row>
                        <Section variant="h3" title="Paiement comptant">
                            <ul>
                                {prices.comptant.fee > 0 && (
                                    <li>
                                        <Typography>
                                            {prices.comptant.fee} € au moment de l'inscription
                                        </Typography>
                                    </li>
                                )}
                                <li>
                                    <Typography>
                                        {prices.comptant.price} € au 
                                        {prices.comptant.date}
                                    </Typography>
                                </li>
                            </ul>
                            <FullWidthColumn>
                                <AmountDisclaimer />
                                <StyledFormControlLabel
                                    control={
                                        <Radio
                                            checked={selectedPaymentMode === 'comptant-cheque'}
                                        />
                                    }
                                    onChange={(_, checked) =>
                                        setSelectedPaymentMode(
                                            checked ? 'comptant-cheque' : undefined,
                                        )
                                    }
                                    label={<PaymentModeLabel paymentMode="comptant-cheque" />}
                                    name={`comptant-cheque-option`}
                                />

                                <StyledFormControlLabel
                                    control={
                                        <Radio
                                            checked={selectedPaymentMode === 'comptant-virement'}
                                        />
                                    }
                                    onChange={(_, checked) =>
                                        setSelectedPaymentMode(
                                            checked ? 'comptant-virement' : undefined,
                                        )
                                    }
                                    label={<PaymentModeLabel paymentMode="comptant-virement" />}
                                    name={`comptant-virement-option`}
                                />
                            </FullWidthColumn>
                        </Section>
                        <Section variant="h3" title="Paiement échelonné">
                            <FullWidthColumn>
                                <ul>
                                    {prices.echelonne.fee > 0 && (
                                        <li>
                                            <Typography>
                                                {INSCRIPTION_FEE} € au moment de l'inscription
                                            </Typography>
                                        </li>
                                    )}
                                    {prices.echelonne.prices.map((payment, index) => (
                                        <li key={payment.date}>
                                            <Typography>
                                                {payment.value.toFixed(0)}€ au {payment.date}
                                            </Typography>
                                        </li>
                                    ))}
                                </ul>
                                <AmountDisclaimer />

                                <StyledFormControlLabel
                                    control={
                                        <Radio
                                            checked={selectedPaymentMode === 'echelonne-cheque'}
                                        />
                                    }
                                    onChange={(_, checked) =>
                                        setSelectedPaymentMode(
                                            checked ? 'echelonne-cheque' : undefined,
                                        )
                                    }
                                    label={<PaymentModeLabel paymentMode="echelonne-cheque" />}
                                    name={`echelonne-cheque-option`}
                                />
                                <StyledFormControlLabel
                                    control={
                                        <Radio
                                            checked={selectedPaymentMode === 'echelonne-virement'}
                                        />
                                    }
                                    onChange={(_, checked) =>
                                        setSelectedPaymentMode(
                                            checked ? 'echelonne-virement' : undefined,
                                        )
                                    }
                                    label={<PaymentModeLabel paymentMode="echelonne-virement" />}
                                    name={`echelonne-virement-option`}
                                />
                            </FullWidthColumn>
                        </Section>
                    </Row>
                    <Row>
                        <TransferDetails />
                    </Row>
                </Section>
            )}
            <Section variant="h2" title="Attestation">
                <StyledFormControlLabel
                    required
                    control={<Checkbox checked={hasAcknowledgedPricing} />}
                    onChange={(_, checked) => setHasAcknowledgedPricing(checked)}
                    label={
                        <BoldParagraph>
                            Je soussigné(e),
                            <DottedUnderlined>
                                {!!formTextData.firstName
                                    ? `${
                                          formTextData.firstName
                                      } ${formTextData.lastName.toUpperCase()}`
                                    : '                                   '}
                            </DottedUnderlined>
                            , confirme avoir pris connaissance des tarifs et des modalités de
                            paiement, et effectue par la présente mon inscription à l’ésaj pour
                            l’année scolaire {CURRENT_INSCRIPTION_YEAR}/
                            {Number(CURRENT_INSCRIPTION_YEAR) + 1}.
                        </BoldParagraph>
                    }
                    name={`has-acknowledged-whole-year-is-due`}
                />
                <StyledFormControlLabel
                    required
                    control={<Checkbox checked={hasAcknowledgedWholeYearIsDue} />}
                    onChange={(_, checked) => setHasAcknowledgedWholeYearIsDue(checked)}
                    label={
                        <BoldParagraph>
                            Je reconnais que l’inscription est annuelle et m’engage au règlement de
                            l’intégralité des frais de scolarité pour l’année 2026/2027, y compris
                            en cas d’abandon ou de désistement en cours d’année. Quel que soit le
                            mode de règlement choisi (comptant ou échelonné), l’intégralité des
                            frais de l’année demeure exigible dès lors que l’inscription est
                            effective.
                        </BoldParagraph>
                    }
                    name={`has-acknowledged-whole-year-is-due`}
                />
                <StyledFormControlLabel
                    required
                    control={<Checkbox checked={hasAcknowledgedCanBeTemporarilyExpelled} />}
                    onChange={(_, checked) => setHasAcknowledgedCanBeTemporarilyExpelled(checked)}
                    label={
                        <BoldParagraph>
                            Je reconnais avoir été informé(e) que, à défaut de règlement intégral
                            avant les échéances fixées, l’ésaj se réserve le droit de suspendre ou
                            de refuser mon accès aux enseignements, et ce jusqu’à complète
                            régularisation de ma situation financière.
                        </BoldParagraph>
                    }
                    name={`has-acknowledged-can-be-temporarily-expelled`}
                />
            </Section>

            <Row>
                <FullWidthColumn>
                    <TextWithWTextField>
                        Fait à
                        <InsertedTextField
                            required
                            label="Lieu"
                            value={formTextData.place}
                            onChange={handleFormChange}
                            name="place"
                            variant="standard"
                        />
                        le <DottedUnderlined>{readableDate}</DottedUnderlined>
                    </TextWithWTextField>
                    <Row>
                        <SignatureFileLabel required htmlFor="signatureFileInput">
                            Téléverser ma signature (.png, .jpg ou .jpeg):
                        </SignatureFileLabel>
                        <input
                            required
                            id="signatureFileInput"
                            type="file"
                            onChange={handleFileChange}
                            accept=".png,.jpg,.jpeg"
                        />
                    </Row>
                </FullWidthColumn>
                <FullWidthCenteredColumn>
                    <EsajSignature />
                </FullWidthCenteredColumn>
            </Row>

            <Row>
                <Button
                    loadingText="Envoi du formulaire en cours... Cela peut prendre quelques secondes."
                    fullWidth
                    variant="contained"
                    isLoading={createFicheInscriptionEntryApiCall.isLoading}
                    type="submit"
                >
                    Envoyer
                </Button>
            </Row>
        </FormLayout>
    );

    function handleFormChange(event: React.ChangeEvent<HTMLInputElement>) {
        const { name, value } = event.target;
        setTextFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    }

    function handleFileChange(e: ChangeEvent<HTMLInputElement>) {
        if (e.target.files) {
            setSignatureFile(e.target.files[0]);
        }
    }

    function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();

        if (wasRegisteredInEsajBefore === undefined) {
            alert("Veuillez indiquer s'il s'agit d'une inscription ou réinscription.");
            return;
        }
        if (selectedPaymentMode === undefined) {
            alert('Veuillez sélectionner une modalité de paiement.');
            return;
        }
        const comptantPrice = prices?.comptant;
        if (!comptantPrice) {
            return;
        }
        if (signatureFile) {
            const formData = new FormData();
            formData.append('file', signatureFile);
            formData.append(
                'data',
                JSON.stringify({
                    ...formTextData,
                    selectedPromo,
                    selectedPaymentMode,
                    hasAlreadyAnEsajMaster,
                    firstInscriptionYear: firstInscriptionYear || CURRENT_INSCRIPTION_YEAR,
                    annualPriceToPay: comptantPrice.price,
                }),
            );
            createFicheInscriptionEntryApiCall.perform(formData);
        } else {
            alert('Veuillez téléverser votre signature avant de soumettre le formulaire.');
            console.error('No file selected');
        }
    }
}

const Row = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
}));
const CenteredRow = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
}));
const FullWidthColumn = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
}));
const FullWidthCenteredColumn = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    alignItems: 'center',
}));

const TextFieldContainer = styled('div')<{ width: string }>(({ theme, width }) => ({
    width,
}));

const StyledFormControlLabel = styled(FormControlLabel)(({ theme }) => ({
    marginRight: theme.spacing(1),
}));

const SignatureFileLabel = styled(FormLabel)(({ theme }) => ({ marginRight: theme.spacing(1) }));

const BoldParagraph = styled('span')(({ theme }) => ({
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    fontWeight: 'bold',
}));

const TextWithWTextField = styled(Typography)(({ theme }) => ({
    display: 'flex',
    alignItems: 'baseline',
    marginBottom: theme.spacing(1),
}));

const InsertedTextField = styled(TextField)(({ theme }) => ({
    marginLeft: theme.spacing(4),
    marginRight: theme.spacing(4),
}));

const DottedUnderlined = styled('span')(({ theme }) => ({
    textDecoration: 'underline',
    textDecorationStyle: 'dotted',
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
}));

export { FicheInscription };
